"use client";

import React from "react";
import styled, { css } from "styled-components";
import mediaQueries from "@/styles/mediaQueries";
import { Dialog, Text } from "@lmn/design-system";
import IconFilters from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconFilters.js";
import IconMap from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconMap.js";
import { SortOptionType } from "@/lib/Types/InspirationSortFilter";
import SortBarMobile from "@/components/Stateful/SortBar/SortBarMobile";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { useMapStore } from "@/lib/stores/mapStore";
import FilterPanel from "../FilterPanel/FilterPanel";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { AppConfig } from "@/lib/Types/AppConfig";

const Container = styled.div<{
  $isHP: boolean;
  $isApp: boolean;
  $hasHeader: boolean;
}>`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  background: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundPage};
  border-bottom: 1px solid
    ${(props) => props.theme.kallisto.color.border.static.borderNeutral};
  box-shadow: ${(props) => props.theme.commons.elevations.medium};
  height: 56px;
  ${mediaQueries.screenMdMin`
    display: none;
  `};
  ${({ $isApp, $hasHeader }) =>
    $isApp &&
    css`
      position: sticky;
      top: ${$hasHeader ? "68px" : "0"};
    `}

  ${({ $isHP }) =>
    $isHP &&
    css`
      position: sticky;
      top: 77px;
    `}
`;

const Spacer = styled.div`
  height: 56px;
  ${mediaQueries.screenMdMin`
    display: none;
  `};
`;

const Button = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  height: 100%;
  color: ${(props) => props.theme.kallisto.color.text.static.textPrimary};

  &:hover {
    background: ${(props) =>
    props.theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }

  &:not(:last-child) {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 38px;
      background: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundLayer01};
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export interface FilterBarProps {
  appConfig: AppConfig;
  sortOptions: SortOptionType[];
  defaultSortOption: SortOptionType;
  hasFlexibleCancellation?: boolean;
  hasSpecialDealsFilter?: boolean;
  hasMapButton?: boolean;
  isHP?: boolean;
  isHotelDetailPage?: boolean;
  isDestinationPage?: boolean;
  isHotelOnly?: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  appConfig,
  sortOptions,
  defaultSortOption,
  hasFlexibleCancellation = false,
  hasSpecialDealsFilter = false,
  hasMapButton = false,
  isHP = false,
  isHotelDetailPage = false,
  isDestinationPage = false,
  isHotelOnly = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const { commitFilters, resetTempFilters } = useFilterState();
  const setShowMap = useMapStore((state) => state.setShowMap);
  const searchParams = useReactiveSearchParams();
  const isApp = searchParams.get("app") === "true";
  // In app mode the header is not rendered for OPX entries (backUrl present),
  // so the bar sticks to the very top instead of below the 64px header bar.
  const hasHeader = !searchParams.get("backUrl");

  const handleApply = () => {
    commitFilters();
    setIsDialogOpen(false);
  };

  const handleCancel = () => {
    resetTempFilters();
    setIsDialogOpen(false);
  };

  return (
    <>
      <Container
        $isHP={isHP}
        $isApp={isApp}
        $hasHeader={hasHeader}
        data-testid='filter-bar'
      >
        <SortBarMobile
          sortOptions={sortOptions}
          defaultSortOption={defaultSortOption}
          envConfig={appConfig.envConfig}
        />

        <Button
          onClick={() => setIsDialogOpen(true)}
          data-testid="openFilterDialogMobile"
        >
          <IconFilters />
          <ButtonText>
            <Text>Filter</Text>
          </ButtonText>
        </Button>

        {hasMapButton && (
          <Button onClick={() => setShowMap(true)}>
            <IconMap />
            <ButtonText>
              <Text>Karte</Text>
            </ButtonText>
          </Button>
        )}
      </Container>

      {/* Filter Dialog */}
      <Dialog
        isOpen={isDialogOpen}
        onClose={handleCancel}
        header={{ title: "Filter", sticky: true }}
        footer={{
          buttonLayoutModifier: {
            default: {
              fullWidth: false,
              reverseOrder: false,
              stacked: false,
            },
            toSm: {
              notStacked: true,
            },
          },
          sticky: true,
          primaryButton: {
            text: "Angebote anzeigen",
            onClick: handleApply,
          },
        }}
      >
        <FilterPanel
          appConfig={appConfig}
          hasSpecialDealsFilter={hasSpecialDealsFilter}
          hasFlexibleCancellation={hasFlexibleCancellation}
          delayedUpdate={true}
          isHotelDetailPage={isHotelDetailPage}
          isDestinationPage={isDestinationPage}
          isHotelOnly={isHotelOnly}
        />
      </Dialog>
      {!isHP && !isApp && <Spacer />}
    </>
  );
};

export default FilterBar;
